/**
 * An operator's own picture, in the same frame as the drawn portrait.
 *
 * Two things here are worth more than the rest of the file.
 *
 * **The fit is computed, not guessed.** The first version hard-coded a vertical
 * nudge that happened to suit one photograph; the next picture was square
 * instead of tall and the same nudge cut her chin off. So the image is measured
 * on load and the cover rectangle is worked out from its real dimensions and a
 * focus point, the way `object-fit: cover` with `object-position` does. Any
 * picture now lands correctly without anybody editing a magic number.
 *
 * **The mouth moves.** A photograph is one flat image, so there is nothing to
 * animate — unless you cut a piece out of it. A soft-edged ellipse over the jaw
 * is drawn a second time on top of the picture and scaled a fraction while she
 * speaks. It is a cheap trick and it is honest about being one: it reads as a
 * face talking at a glance, and it will not survive close inspection, because
 * the pixels are the same pixels.
 */
import { useEffect, useState } from "react";
import s from "./avatar.module.css";
import type { MarkState } from "./types";
import { t } from "../i18n";

/**
 * Where a shipped portrait lives, if there is one.
 *
 * Referenced rather than required: drop a file at that path and every operator
 * gets it; leave it absent and the load fails, the caller is told, and the drawn
 * portrait takes over. Nothing breaks either way.
 */
export const DEFAULT_PORTRAIT = "/assets/assistant2.jpg";

/**
 * How the picture sits in the card, and where her mouth is in it.
 *
 * `focus` follows CSS `object-position`: a fraction of the overflow, so 0.5 is
 * centred and 0 pins the top or left edge. `mouth` is in the card's own 300×200
 * units, and is what the talking animation moves.
 *
 * These are the only numbers tied to a particular photograph. Swap the picture
 * and these are what to adjust — nothing else in the module knows or cares.
 */
export interface PortraitFit {
  focus: { x: number; y: number };
  mouth: { x: number; y: number; rx: number; ry: number };
  /**
   * Where her face sits in the card, for the small rendering to crop around.
   *
   * Derived from the mouth at first, which put the corner crop on her nose and
   * chin — a mouth is at the bottom of a face, not the middle of one. Stating
   * it separately costs one line and is the difference between a recognisable
   * thumbnail and a close-up of somebody's jaw.
   */
  face: { x: number; y: number };
}

export const DEFAULT_FIT: PortraitFit = {
  /* assistant2.jpg is square and framed tight on the face; centred vertically
     puts her eyes in the upper third and keeps her chin inside the card. */
  focus: { x: 0.5, y: 0.5 },
  /* Measured off the rendered card rather than guessed: her lips land at
     (141, 146) in the card's own units, and the ellipse reaches from the base
     of the nose to below the chin so the whole jaw travels together. */
  mouth: { x: 141, y: 146, rx: 50, ry: 34 },
  face: { x: 140, y: 96 },
};

const CENTRED_FIT: PortraitFit = {
  focus: { x: 0.5, y: 0.35 },
  mouth: { x: 150, y: 140, rx: 46, ry: 30 },
  face: { x: 150, y: 95 },
};

const SAYS: Record<MarkState, string> = {
  idle: "Sky, ready",
  listening: "Sky, listening",
  thinking: "Sky, working",
  speaking: "Sky, speaking",
};

/** The cover rectangle for an image of these dimensions inside the 300×200 card. */
function cover(iw: number, ih: number, focus: { x: number; y: number }) {
  const scale = Math.max(300 / iw, 200 / ih);
  const w = iw * scale;
  const h = ih * scale;
  return { x: (300 - w) * focus.x, y: (200 - h) * focus.y, w, h };
}

export function Portrait({
  state,
  src,
  width = 288,
  className = "",
  fit,
  onClick,
  title,
  onFailed,
}: {
  state: MarkState;
  src: string;
  width?: number;
  className?: string;
  fit?: PortraitFit;
  onClick?: () => void;
  title?: string;
  /** Called when the file will not load, so the caller can fall back to the drawing. */
  onFailed?: () => void;
}) {
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const [broken, setBroken] = useState(false);

  /* Measured before it is drawn: the cover rectangle needs the real dimensions,
     and an SVG <image> will not tell you what they are. */
  useEffect(() => {
    let live = true;
    const probe = new Image();
    probe.onload = () => live && setSize({ w: probe.naturalWidth, h: probe.naturalHeight });
    probe.onerror = () => {
      if (!live) return;
      setBroken(true);
      onFailed?.();
    };
    probe.src = src;
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  if (broken) return null;

  const f = fit ?? (src === DEFAULT_PORTRAIT ? DEFAULT_FIT : CENTRED_FIT);
  const small = width < 120;
  const rect = size ? cover(size.w, size.h, f.focus) : { x: 0, y: 0, w: 300, h: 200 };

  /*
   * The square the small rendering crops to.
   *
   * 160 rather than 104, which was the first guess and was simply too small:
   * in this card a face runs about 150 units from brow to chin, so a 104 box
   * centred on it holds the nose and nothing else. The square has to be about
   * as tall as the face, or the thumbnail is a close-up of the middle of one.
   */
  const side = 160;
  const cropX = Math.max(0, Math.min(300 - side, f.face.x - side / 2));
  const cropY = Math.max(0, Math.min(200 - side, f.face.y - side / 2));
  const box = small
    ? { view: `${cropX.toFixed(1)} ${cropY.toFixed(1)} ${side} ${side}`, w: width, h: width }
    : { view: "0 0 300 200", w: width, h: Math.round((width * 2) / 3) };

  /* Two of these can be on screen at once — the big card and the small one —
     and duplicate gradient ids would make the second silently take the first's. */
  const uid = small ? "sm" : "lg";

  return (
    <svg
      className={`${s.avatar} ${small ? s.small : ""} ${s[`is_${state}`]} ${onClick ? s.clickable : ""} ${className}`}
      width={box.w}
      height={box.h}
      viewBox={box.view}
      role={onClick ? "button" : "img"}
      tabIndex={onClick ? 0 : undefined}
      aria-label={title ?? t(SAYS[state])}
      onClick={onClick}
      onKeyDown={onClick ? (e) => (e.key === "Enter" || e.key === " ") && onClick() : undefined}
    >
      <defs>
        <radialGradient id={`pt-bg-${uid}`} cx="50%" cy="42%" r="72%">
          <stop offset="0%" stopColor="#1f66a8" />
          <stop offset="52%" stopColor="#0f3f73" />
          <stop offset="100%" stopColor="#07233f" />
        </radialGradient>
        <clipPath id={`pt-card-${uid}`}>
          <rect x="2" y="2" width="296" height="196" rx="12" />
        </clipPath>
        {/*
         * The overlay grid is masked away from the middle. Drawn flat across the
         * whole card it lies over her face like a mesh — which is what a grid
         * *on* a photograph looks like, rather than a grid behind her.
         */}
        <radialGradient id={`pt-gridfade-${uid}`} cx="50%" cy="44%" r="58%">
          <stop offset="0%" stopColor="#000000" />
          <stop offset="55%" stopColor="#222222" />
          <stop offset="100%" stopColor="#ffffff" />
        </radialGradient>
        <mask id={`pt-gridmask-${uid}`}>
          <rect x="0" y="0" width="300" height="200" fill={`url(#pt-gridfade-${uid})`} />
        </mask>
        <radialGradient id={`pt-vig-${uid}`} cx="50%" cy="46%" r="62%">
          <stop offset="58%" stopColor="#05142a" stopOpacity="0" />
          <stop offset="100%" stopColor="#04101f" stopOpacity="0.72" />
        </radialGradient>
        {/* the jaw cut-out: soft at the edges so the moving piece shows no seam */}
        <radialGradient id={`pt-mouthfade-${uid}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="58%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#000000" />
        </radialGradient>
        <mask id={`pt-mouthmask-${uid}`}>
          <ellipse cx={f.mouth.x} cy={f.mouth.y} rx={f.mouth.rx} ry={f.mouth.ry} fill={`url(#pt-mouthfade-${uid})`} />
        </mask>
      </defs>

      <g clipPath={`url(#pt-card-${uid})`}>
        <rect x="2" y="2" width="296" height="196" fill={`url(#pt-bg-${uid})`} />

        {/* the grid behind: what a cut-out image is placed against */}
        <g className={s.grid} stroke="#05080f" fill="none">
          {Array.from({ length: 13 }, (_, i) => i * 16).map((y) => (
            <line key={`h${y}`} x1="0" y1={y} x2="300" y2={y} strokeWidth="1.1" opacity="0.42" />
          ))}
          {Array.from({ length: 20 }, (_, i) => i * 16).map((x) => (
            <line key={`v${x}`} x1={x} y1="0" x2={x} y2="126" strokeWidth="1.1" opacity="0.42" />
          ))}
          {[130, 140, 152, 167, 185, 206].map((y, i) => (
            <line key={`f${y}`} x1="-30" y1={y} x2="330" y2={y} strokeWidth={1 + i * 0.22} opacity="0.5" />
          ))}
          {[-230, -140, -60, 10, 74, 150, 226, 290, 360, 440, 530].map((x) => (
            <line key={`c${x}`} x1={x} y1="210" x2={150 + (x - 150) * 0.12} y2="127" strokeWidth="1.1" opacity="0.45" />
          ))}
        </g>

        {/* her, covering the card without distortion */}
        <g className={s.figure}>
          <image href={src} x={rect.x} y={rect.y} width={rect.w} height={rect.h} preserveAspectRatio="none" />
        </g>

        {/*
         * The same pixels again, masked to her jaw, moved a fraction while she
         * speaks. Nothing else in a photograph can move — this is the whole of
         * the effect, and it is why it is subtle rather than a gape.
         */}
        <g
          className={s.talk}
          mask={`url(#pt-mouthmask-${uid})`}
          style={{ transformOrigin: `${f.mouth.x}px ${f.mouth.y - f.mouth.ry}px` }}
        >
          <image href={src} x={rect.x} y={rect.y} width={rect.w} height={rect.h} preserveAspectRatio="none" />
        </g>

        {/* the grid again, faintly, for an opaque photograph that covered the first */}
        <g className={s.grid} stroke="#04070d" fill="none" opacity="0.34" mask={`url(#pt-gridmask-${uid})`}>
          {Array.from({ length: 13 }, (_, i) => i * 16).map((y) => (
            <line key={`oh${y}`} x1="0" y1={y} x2="300" y2={y} strokeWidth="1" />
          ))}
          {Array.from({ length: 20 }, (_, i) => i * 16).map((x) => (
            <line key={`ov${x}`} x1={x} y1="0" x2={x} y2="200" strokeWidth="1" />
          ))}
        </g>
        {/* A blue unifying wash. Light: heavier drains the warmth out of skin and
            the picture stops looking like a photograph. */}
        <rect x="2" y="2" width="296" height="196" fill="#1f66a8" opacity="0.08" />
        <rect x="2" y="2" width="296" height="196" fill={`url(#pt-vig-${uid})`} />

        <g className={s.scan} opacity="0.07">
          {Array.from({ length: 50 }, (_, i) => (
            <line key={i} x1="0" y1={i * 4} x2="300" y2={i * 4} stroke="#dff2ff" strokeWidth="0.9" />
          ))}
        </g>
      </g>

      <rect className={s.frame} x="2" y="2" width="296" height="196" rx="12" fill="none" strokeWidth="3" />
      <rect
        className={s.frameArc}
        x="2"
        y="2"
        width="296"
        height="196"
        rx="12"
        fill="none"
        strokeWidth="3.5"
        strokeLinecap="round"
        pathLength={100}
        strokeDasharray="16 84"
      />
    </svg>
  );
}
